import { createSelector } from '@reduxjs/toolkit';

import { AggregatedTrussStatistics } from '../components/pages/gridTypes';
import { RootState } from '../store';
import { getAggregatedTrussStatisticsByBundleGuid } from './bundles';
import { getBundlesByScheduleGroupGuid } from './getBundlesByScheduleGroupGuid';
import { getProductionComponentInstancesByBundleGuid } from './getProductionComponentInstancesByBundleGuid';
import { getStatusPercentages, StatusPercentage } from './getStatusPercentages';
import {
  getGroupedProductionComponentInstancesByBundleGuid,
  getProductionComponentInstancesByScheduleGroupGuid,
} from './productionComponentInstances';
import { getAggregatedTrussStatisticsByScheduleGroupGuid } from './scheduleGroups';

interface BasicRowInformation {
  readonly type: 'scheduleGroup' | 'bundle' | 'groupedProductionComponentInstance';
  readonly rowId: string;
  readonly guid: string;
  readonly name: string;
  readonly statusPercentages: readonly StatusPercentage[];
  readonly line: string | null;
  readonly truckload: string | null;
}

interface ScheduleGroupGridRow extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'scheduleGroup';
  readonly targetDate: string | null;
}

export interface BundleGridRow extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'bundle';
  readonly scheduleGroupGuid: string;
}

export interface GroupedProductionComponentInstanceGridRow extends BasicRowInformation, AggregatedTrussStatistics {
  readonly type: 'groupedProductionComponentInstance';
  readonly bundleGuid: string;
  readonly analysisSetGuid: string;
}

export type GridRow = ScheduleGroupGridRow | BundleGridRow | GroupedProductionComponentInstanceGridRow;
export type GridRowKey = keyof GridRow;

export function getScheduleGroupRowId(scheduleLineGroupGuid: string) {
  return `scheduleGroup.${scheduleLineGroupGuid}`;
}
export function isScheduleGroupRow(rowId: string) {
  return rowId.startsWith('scheduleGroup.');
}
export function getScheduleGroupGuidFromRowId(rowId: string) {
  if (!isScheduleGroupRow(rowId)) throw new Error('Tried to get the schedule group GUID of a non-schedule-group row');
  return rowId.split('.')[1];
}
export function getBundleRowId(bundleGuid: string) {
  return `bundle.${bundleGuid}`;
}
export function isBundleRow(rowId: string) {
  return rowId.startsWith('bundle.');
}
export function getBundleGuidFromRowId(rowId: string) {
  if (!isBundleRow(rowId)) throw new Error('Tried to get the bundle GUID of a non-bundle row');
  return rowId.substring('bundle.'.length);
}
export function getGroupedProductionComponentInstanceRowId(bundleGuid: string, analysisSetGuid: string) {
  return `groupedProductionComponentInstance.${bundleGuid}.${analysisSetGuid}`;
}
export function isGroupedProductionComponentInstanceRow(rowId: string) {
  return rowId.startsWith('groupedProductionComponentInstance.');
}

export const getScheduleGroupGridRowsByScheduleGroupGuid = createSelector(
  (state: RootState) => state.scheduleGroups,
  getBundlesByScheduleGroupGuid,
  getAggregatedTrussStatisticsByBundleGuid,
  getAggregatedTrussStatisticsByScheduleGroupGuid,
  getProductionComponentInstancesByBundleGuid,
  getProductionComponentInstancesByScheduleGroupGuid,
  getGroupedProductionComponentInstancesByBundleGuid,
  (state: RootState) => state.scheduleLineGroups,
  (state: RootState) => state.lines,
  (state: RootState) => state.truckloads,

  (
    scheduleGroups,
    bundlesByScheduleGroupGuid,
    aggregatedTrussStatisticsByBundleGuid,
    aggregatedTrussStatisticsByScheduleLineGroupGuid,
    productionComponentInstancesByBundleGuid,
    productionComponentInstancesByScheduleGroupGuid,
    groupedProductionComponentInstancesByBundleGuid,
    scheduleLineGroups,
    lines,
    truckloads,
  ): { [scheduleGroupGuid: string]: readonly GridRow[] } => {
    const gridRowsByScheduleGroupGuid: { [scheduleGroupGuid: string]: readonly GridRow[] } = {};

    for (const scheduleGroup of Object.values(scheduleGroups)) {
      const rowsForScheduleGroup = [];
      const numberOfBundlesScheduledToLine = bundlesByScheduleGroupGuid[scheduleGroup.guid].filter(
        (bundle) => bundle.scheduleLineGroupGuid !== null,
      ).length;
      const percentageOfBundlesScheduledToLine = Math.round(
        (numberOfBundlesScheduledToLine / bundlesByScheduleGroupGuid[scheduleGroup.guid].length) * 100,
      );
      const numberOfBundlesAssignedToTruckload = bundlesByScheduleGroupGuid[scheduleGroup.guid].filter(
        (bundle) => bundle.truckloadGuid !== null,
      ).length;
      const percentageOfBundlesAssignedToTruckload = Math.round(
        (numberOfBundlesAssignedToTruckload / bundlesByScheduleGroupGuid[scheduleGroup.guid].length) * 100,
      );
      const scheduleGroupRow: GridRow = {
        type: 'scheduleGroup',
        rowId: getScheduleGroupRowId(scheduleGroup.guid),
        guid: scheduleGroup.guid,
        name: scheduleGroup.name,
        ...aggregatedTrussStatisticsByScheduleLineGroupGuid[scheduleGroup.guid],
        statusPercentages: getStatusPercentages(productionComponentInstancesByScheduleGroupGuid[scheduleGroup.guid]),
        line: `${percentageOfBundlesScheduledToLine}%`,
        truckload: `${percentageOfBundlesAssignedToTruckload}%`,
        targetDate: scheduleGroup.targetDate,
      };
      rowsForScheduleGroup.push(scheduleGroupRow);

      const bundleRowsForScheduleGroup: BundleGridRow[] = [];
      for (const bundle of bundlesByScheduleGroupGuid[scheduleGroup.guid]) {
        bundleRowsForScheduleGroup.push({
          type: 'bundle',
          rowId: getBundleRowId(bundle.guid),
          guid: bundle.guid,
          name: `Bundle ${bundle.id}`,
          scheduleGroupGuid: scheduleGroup.guid,
          line:
            bundle.scheduleLineGroupGuid === null
              ? null
              : lines[scheduleLineGroups[bundle.scheduleLineGroupGuid]?.lineGuid]?.name,
          ...aggregatedTrussStatisticsByBundleGuid[bundle.guid],
          statusPercentages: getStatusPercentages(productionComponentInstancesByBundleGuid[bundle.guid]),
          truckload: bundle.truckloadGuid === null ? null : (truckloads[bundle.truckloadGuid]?.id.toString() ?? null),
        });
      }
      rowsForScheduleGroup.push(...bundleRowsForScheduleGroup);

      const groupedProductionComponentInstanceRowsForScheduleLineGroup: readonly GroupedProductionComponentInstanceGridRow[] =
        bundlesByScheduleGroupGuid[scheduleGroup.guid].flatMap((bundle) =>
          groupedProductionComponentInstancesByBundleGuid[bundle.guid].map(
            (
              {
                guid,
                analysisSetGuid,
                trussName,
                trussCount,
                boardFeet,
                linearFeet,
                instances,
                widths,
                heights,
                thicknesses,
              },
              index,
            ) => ({
              type: 'groupedProductionComponentInstance',
              rowId: getGroupedProductionComponentInstanceRowId(bundle.guid, analysisSetGuid),
              guid,
              name: trussName,
              bundleGuid: bundle.guid,
              analysisSetGuid,
              priority: index,
              trussCount,
              boardFeet,
              linearFeet,
              widths,
              heights,
              thicknesses,
              statusPercentages: getStatusPercentages(instances),
              line:
                bundle.scheduleLineGroupGuid === null
                  ? null
                  : lines[scheduleLineGroups[bundle.scheduleLineGroupGuid]?.lineGuid]?.name,
              truckload:
                bundle.truckloadGuid === null ? null : (truckloads[bundle.truckloadGuid]?.id.toString() ?? null),
            }),
          ),
        );
      rowsForScheduleGroup.push(...groupedProductionComponentInstanceRowsForScheduleLineGroup);

      gridRowsByScheduleGroupGuid[scheduleGroup.guid] = rowsForScheduleGroup;
    }

    return gridRowsByScheduleGroupGuid;
  },
);
